import { useState, useEffect, useRef, useMemo } from "react";
import { useDataProvider } from "ra-core";
import {
  format,
  parseISO,
  isSameDay,
  isToday,
  addDays,
  subDays,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import type { Task } from "../types";
import { getActivityTypeConfig } from "./activityTypes";
import { cn } from "@/lib/utils";

const HOUR_HEIGHT = 44; // 44px por hora
const MINUTE_HEIGHT = HOUR_HEIGHT / 60;
const HOURS = Array.from({ length: 24 }, (_, i) => i);

interface ActivityMiniDayTimelineProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  startTime?: string; // "HH:mm"
  endTime?: string; // "HH:mm"
  currentType: string;
  currentTitle?: string;
  onSelectSlot: (startHour: number, startMinute: number) => void;
  excludeTaskId?: string | number;
  /** Se fornecido, filtra as atividades exibidas pelo vendedor selecionado */
  salesId?: string | number | null;
  /** Nome do proprietário da atividade para exibir no cabeçalho */
  ownerName?: string | null;
}

export const ActivityMiniDayTimeline = ({
  selectedDate,
  onDateChange,
  startTime,
  endTime,
  currentType,
  currentTitle,
  onSelectSlot,
  excludeTaskId,
  salesId,
  ownerName,
}: ActivityMiniDayTimelineProps) => {
  const dataProvider = useDataProvider();
  const [dayTasks, setDayTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const typeConfig = getActivityTypeConfig(currentType);
  const TypeIcon = typeConfig.icon;

  // Carrega tarefas para o dia selecionado
  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    dataProvider
      .getList("tasks", {
        pagination: { page: 1, perPage: 250 },
        sort: { field: "data_inicio", order: "ASC" },
        filter: {},
      })
      .then((res) => {
        if (!isMounted) return;
        const allTasks = (res.data as Task[]) || [];
        const filtered = allTasks.filter((t) => {
          if (excludeTaskId && String(t.id) === String(excludeTaskId))
            return false;
          // Filtra por proprietário se salesId for fornecido
          if (salesId != null && String(t.sales_id) !== String(salesId))
            return false;
          const dateStr = t.data_inicio || t.due_date;
          if (!dateStr) return false;
          try {
            return isSameDay(parseISO(dateStr), selectedDate);
          } catch {
            return false;
          }
        });
        setDayTasks(filtered);
      })
      .catch((err) => {
        console.error("Erro ao carregar atividades do dia:", err);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [selectedDate, excludeTaskId, salesId, dataProvider]);

  // Scroll automático para ~08:00 ou horário do evento
  useEffect(() => {
    if (scrollRef.current) {
      let targetHour = 8;
      if (startTime) {
        const [h] = startTime.split(":").map(Number);
        if (!isNaN(h)) targetHour = Math.max(0, h - 1);
      }
      scrollRef.current.scrollTop = targetHour * HOUR_HEIGHT;
    }
  }, [selectedDate]);

  // Processa os eventos existentes para posicionamento
  const parsedDayEvents = useMemo(() => {
    return dayTasks.map((task) => {
      const startStr =
        task.data_inicio || task.due_date || new Date().toISOString();
      const startDate = parseISO(startStr);
      const startMinutes = startDate.getHours() * 60 + startDate.getMinutes();

      let durationMinutes = 45;
      if (task.data_fim) {
        const endDate = parseISO(task.data_fim);
        durationMinutes = Math.max(
          15,
          Math.round((endDate.getTime() - startDate.getTime()) / (60 * 1000)),
        );
      }

      const top = startMinutes * MINUTE_HEIGHT;
      const height = Math.max(22, durationMinutes * MINUTE_HEIGHT);

      return {
        task,
        top,
        height,
        startTimeStr: format(startDate, "HH:mm"),
        endTimeStr: task.data_fim
          ? format(parseISO(task.data_fim), "HH:mm")
          : undefined,
      };
    });
  }, [dayTasks]);

  // Posicionamento do Preview da Atividade Atual que está sendo editada/criada
  const currentPreview = useMemo(() => {
    if (!startTime) return null;
    const [startH, startM] = startTime.split(":").map(Number);
    if (isNaN(startH) || isNaN(startM)) return null;

    const startMinutes = startH * 60 + startM;
    let endMinutes = startMinutes + 60;
    if (endTime) {
      const [endH, endM] = endTime.split(":").map(Number);
      if (!isNaN(endH) && !isNaN(endM)) {
        endMinutes = endH * 60 + endM;
        if (endMinutes <= startMinutes) endMinutes = startMinutes + 30;
      }
    }

    const duration = endMinutes - startMinutes;
    const top = startMinutes * MINUTE_HEIGHT;
    const height = Math.max(24, duration * MINUTE_HEIGHT);

    return {
      top,
      height,
      duration,
      startTime,
      endTime,
    };
  }, [startTime, endTime]);

  // Indicador de hora atual (Agora)
  const now = new Date();
  const isSelectedToday = isToday(selectedDate);
  const currentNowMinutes = now.getHours() * 60 + now.getMinutes();
  const currentNowTop = currentNowMinutes * MINUTE_HEIGHT;

  return (
    <div className="flex flex-col h-full min-h-[460px] max-h-[80vh]">
      {/* Cabeçalho de Navegação de Data */}
      <div className="flex items-center justify-between pb-2.5 border-b border-border/60 gap-2">
        <div className="min-w-0">
          <h4 className="text-xs font-semibold capitalize text-foreground truncate">
            {format(selectedDate, "EEEE, d 'de' MMMM", { locale: ptBR })}
          </h4>
          <p className="text-[10px] text-muted-foreground">
            {loading
              ? "Carregando horários..."
              : `${dayTasks.length} ${dayTasks.length === 1 ? "atividade agendada" : "atividades agendadas"}`}
          </p>
          {ownerName && (
            <span className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-violet-500/10 text-violet-600 dark:text-violet-400 border border-violet-500/20">
              👤 {ownerName}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={() => onDateChange(subDays(selectedDate, 1))}
            className="p-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            title="Dia anterior"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => onDateChange(new Date())}
            className="px-1.5 py-0.5 text-[10px] font-medium rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
          >
            Hoje
          </button>
          <button
            type="button"
            onClick={() => onDateChange(addDays(selectedDate, 1))}
            className="p-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            title="Próximo dia"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Pill Indicadora da Atividade em Criação */}
      <div className="my-2 shrink-0">
        <div className="bg-primary text-primary-foreground px-3 py-1.5 rounded-lg flex items-center gap-2 shadow-xs text-xs font-medium">
          <TypeIcon className="w-3.5 h-3.5 shrink-0" />
          <span className="truncate">
            {typeConfig.label}: {currentTitle?.trim() || "Nova Atividade"}
          </span>
          {startTime && (
            <span className="ml-auto text-[11px] font-mono opacity-90 shrink-0">
              {startTime} {endTime ? `→ ${endTime}` : ""}
            </span>
          )}
        </div>
      </div>

      {/* Grade de Horários (Timeline) */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto relative rounded-lg border border-border/60 bg-card/60 select-none"
      >
        <div
          className="relative w-full"
          style={{ height: `${24 * HOUR_HEIGHT}px` }}
        >
          {/* Linhas das Horas e Slots */}
          {HOURS.map((hour) => (
            <div
              key={hour}
              className="absolute left-0 right-0 border-t border-border/40 flex items-start group/hour hover:bg-primary/5 transition-colors"
              style={{
                top: `${hour * HOUR_HEIGHT}px`,
                height: `${HOUR_HEIGHT}px`,
              }}
            >
              {/* Rótulo da Hora */}
              <div className="w-11 shrink-0 text-[10px] font-mono text-muted-foreground/80 pl-1 pt-0.5 select-none">
                {String(hour).padStart(2, "0")}:00
              </div>

              {/* Botão de Agendamento Rápido no Slot */}
              <div
                className="flex-1 h-full cursor-pointer relative"
                onClick={() => onSelectSlot(hour, 0)}
                title={`Clique para definir o horário às ${String(hour).padStart(2, "0")}:00`}
              >
                {/* Linha de 30 minutos discreta */}
                <div
                  className="absolute left-0 right-0 border-t border-dashed border-border/20"
                  style={{ top: `${HOUR_HEIGHT / 2}px` }}
                />

                <div className="opacity-0 group-hover/hour:opacity-100 transition-opacity absolute right-2 top-1.5 flex items-center gap-1 text-[10px] text-primary font-medium bg-primary/10 px-1.5 py-0.5 rounded border border-primary/20 pointer-events-none">
                  <Plus className="w-3 h-3" />
                  <span>{String(hour).padStart(2, "0")}:00</span>
                </div>
              </div>
            </div>
          ))}

          {/* Linha Vermelha de Hora Atual (Hoje) */}
          {isSelectedToday && (
            <div
              className="absolute left-0 right-0 z-20 flex items-center pointer-events-none"
              style={{ top: `${currentNowTop}px` }}
            >
              <span className="text-[9px] font-mono font-bold text-red-500 bg-card/90 px-1 rounded shadow-xs ml-0.5">
                {format(now, "HH:mm")}
              </span>
              <div className="w-1.5 h-1.5 rounded-full bg-red-500 ring-2 ring-red-500/20" />
              <div className="flex-1 border-t border-red-500" />
            </div>
          )}

          {/* Atividades Já Agendadas no Dia */}
          {parsedDayEvents.map(
            ({ task, top, height, startTimeStr, endTimeStr }) => {
              const config = getActivityTypeConfig(task.type);
              const Icon = config.icon;
              return (
                <div
                  key={task.id}
                  className={cn(
                    "absolute left-12 right-2 rounded-md border p-1 z-10 overflow-hidden shadow-2xs transition-all",
                    config.colorClass,
                  )}
                  style={{
                    top: `${top}px`,
                    height: `${height}px`,
                  }}
                  title={`${task.text} (${startTimeStr} ${endTimeStr ? `→ ${endTimeStr}` : ""})`}
                >
                  <div className="flex items-center gap-1 min-w-0">
                    <Icon className="w-3 h-3 shrink-0" />
                    <span className="text-[11px] font-semibold truncate leading-tight">
                      {task.text}
                    </span>
                  </div>
                  {height >= 34 && (
                    <div className="text-[9px] font-mono opacity-85 mt-0.5">
                      {startTimeStr} {endTimeStr ? `→ ${endTimeStr}` : ""}
                    </div>
                  )}
                </div>
              );
            },
          )}

          {/* Preview da Atividade Sendo Criada / Editada */}
          {currentPreview && (
            <div
              className="absolute left-12 right-2 rounded-md border-2 border-primary bg-primary/20 text-primary p-1 z-15 shadow-sm overflow-hidden animate-pulse-subtle pointer-events-none"
              style={{
                top: `${currentPreview.top}px`,
                height: `${currentPreview.height}px`,
              }}
            >
              <div className="flex items-center gap-1 min-w-0">
                <TypeIcon className="w-3 h-3 shrink-0 text-primary" />
                <span className="text-[11px] font-bold truncate leading-tight text-foreground">
                  {currentTitle?.trim() || typeConfig.label} (Novo)
                </span>
              </div>
              {currentPreview.height >= 32 && (
                <div className="text-[9px] font-mono font-medium text-foreground/90 mt-0.5">
                  {currentPreview.startTime} → {currentPreview.endTime}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="pt-2 text-[10px] text-muted-foreground text-center">
        💡 Clique em qualquer horário livre na grade para selecioná-lo
        automaticamente
      </div>
    </div>
  );
};
